import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, FormGroup } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { capitalizeFirstLetter, getUserSelected } from '../../utils';
import * as operatorActions from '../../actions/operatorActions';
import * as erpTypeActions from '../../actions/erpTypeActions';
import * as userActions from '../../actions/userActions';
import XMAuditTable from '../../components/XMAuditTable';
import { getAuth, TotpMultiFactorGenerator, multiFactor, sendEmailVerification } from 'firebase/auth';
import QRCode from 'qrcode';
import { isProduction, envName } from '../../utils/environment';

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

const FieldError = ({ error }) => (error ? <div className="mt-1 text-danger">{error.message}</div> : null);

// Changing your own role invalidates your session, so the save logs you out. userReducer starts
// selected at null, so a session that never completed its login has no one to compare against.
export const shouldLogOut = (op, selectedUser) =>
  !!selectedUser && op.id === selectedUser.id && op.role !== selectedUser.role;

// XMCrud renders this form twice from the same operator.one state: once in the Create modal
// with isEdit false, once in the Edit dock with isEdit true. A getOne response that lands after
// the Create modal opens puts that user's id in the values, so Save would update them. Create
// drops the id. Root.js renders the profile form without isEdit, and that save keeps its id.
export const buildOperator = (values, { isEdit, dataCentreSelected, dataCentre }) => {
  const op = { ...values };
  if (isEdit === false) delete op.id;
  if (dataCentreSelected) {
    op.dataCentre = { id: dataCentreSelected };
  } else if (!op.id) {
    op.dataCentre = { id: dataCentre.selected.id };
  }
  if (!op.dataCentre) {
    op.dataCentre = { id: dataCentre.selected.id ?? dataCentre.all?.[0]?.id };
  }
  return op;
};

// Migrated from redux-form to react-hook-form.
const OperatorForm = ({ initialValues, isEdit, profileSetting, handleCancel, toggle }) => {
  const dispatch = useDispatch();
  const config = useSelector((s) => s.config);
  const operator = useSelector((s) => s.operator);
  const erpType = useSelector((s) => s.erpType);
  const dataCentre = useSelector((s) => s.dataCentre);
  const selectedUser = useSelector((s) => s.user.selected);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: initialValues || {} });

  const [dataCentreSelected, setDataCentreSelected] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const totpSecret = useRef(null);
  const userSelected = getUserSelected();

  useEffect(() => {
    if (!erpType.fetched) dispatch(erpTypeActions.getAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (values) => {
    const op = buildOperator(values, { isEdit, dataCentreSelected, dataCentre });
    dispatch(operatorActions.save(op)).then(() => {
      if (shouldLogOut(op, selectedUser)) {
        dispatch(userActions.logOut());
      }
      toggle();
    });
  };

  const onCancel = () => {
    setShowVerification(false);
    handleCancel();
    toggle();
  };

  const activate2FA = () => {
    if (getAuth().currentUser == null) return;
    multiFactor(getAuth().currentUser)
      .getSession()
      .then((multiFactorSession) => {
        TotpMultiFactorGenerator.generateSecret(multiFactorSession)
          .then((secret) => {
            totpSecret.current = secret;
            const totpIssuerName = isProduction() ? 'CB Store Manager' : 'CB Store Manager-' + envName().substring(0, 3);
            const totpUri = secret.generateQrCodeUrl(getAuth().currentUser.email, totpIssuerName);
            QRCode.toCanvas(document.getElementById('canvas'), totpUri)
              .then(() => setShowVerification(true))
              .catch((e) => console.error(e));
          })
          .catch((e) => {
            if (e.code === 'auth/unverified-email') {
              sendEmailVerification(getAuth().currentUser).then(() => {});
              dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: 'Need to verify email first before enrolling second factors.' });
            } else if (e.code === 'auth/maximum-second-factor-count-exceeded') {
              dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: '2FA has been enrolled for this account.' });
            } else if (e.code === 'auth/requires-recent-login') {
              dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: 'Re-authentication required to enrolled for 2FA.' });
            } else {
              dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: e.message });
            }
          });
      })
      .catch((e) => {
        console.error(e);
        dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: e.message });
      });
  };

  const verify2FA = () => {
    const otpValue = (document.getElementById('otp') || {}).value;
    if (!otpValue) {
      dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: 'Verification Code is required.' });
      return;
    }
    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret.current, otpValue);
    multiFactor(getAuth().currentUser)
      .enroll(multiFactorAssertion, getAuth().currentUser.displayName)
      .then(() => {
        dispatch({ type: 'OPERATOR_SAVE_SUCCESS' });
        setShowVerification(false);
        handleCancel();
        toggle();
      })
      .catch((e) => {
        let errMsg = e.message;
        if (e.code === 'auth/invalid-verification-code') errMsg = '2FA Verification Code is incorrect or expired';
        dispatch({ type: 'OPERATOR_SAVE_ERROR', payload: errMsg });
      });
  };

  return (
    <div>
      <div>
        <form onSubmit={handleSubmit(onSubmit)} className="form-horizontal" autoComplete="new-password">
          {operator.error && <Alert color="danger">{operator.error}</Alert>}
          <FormGroup row>
            <Col md="3">
              <label>Name</label>
            </Col>
            <Col md="9">
              <input
                {...register('name', { required: 'Required' })}
                type="text"
                className="form-control"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="off"
              />
              <FieldError error={errors.name} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label>Username</label>
            </Col>
            <Col md="9">
              <input
                {...register('username', { required: 'Required', pattern: { value: EMAIL_RE, message: 'Invalid email address' } })}
                type="text"
                className="form-control"
                disabled={isEdit}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="off"
              />
              <FieldError error={errors.username} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label>Password</label>
            </Col>
            <Col md="9">
              <input
                {...register('password', isEdit ? {} : { required: 'Required' })}
                type="password"
                className="form-control"
                autoComplete="sdfdsfds"
                autoCorrect="off"
                spellCheck="off"
              />
              <FieldError error={errors.password} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="password2">Confirm Password</label>
            </Col>
            <Col md="9">
              <input
                {...register('password2', {
                  required: isEdit ? false : 'Required',
                  validate: (v) => v === getValues('password') || 'Passwords dont match',
                })}
                type="password"
                className="form-control"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="off"
              />
              <FieldError error={errors.password2} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="phone">Phone</label>
            </Col>
            <Col md="9">
              <input {...register('phone')} type="text" className="form-control" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="dataCentre">Default Data Center</label>
            </Col>
            <Col md="9">
              <select
                {...register('dataCentre.id', { onChange: (e) => setDataCentreSelected(e.target.value) })}
                className="form-control"
                disabled={userSelected.role === 'PARTNER'}
              >
                {userSelected.role !== 'PARTNER' ? (
                  dataCentre.all.map((dc) => (
                    <option key={dc.id} value={dc.id}>
                      {dc.name}
                    </option>
                  ))
                ) : (
                  <option value={dataCentre.selected.id}>{dataCentre.selected.name}</option>
                )}
              </select>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="role">Role</label>
            </Col>
            <Col md="9">
              <select {...register('role')} className="form-control" disabled={userSelected.role === 'PARTNER'}>
                {userSelected.role !== 'PARTNER' ? (
                  config.enums &&
                  config.enums['userRole']
                    .filter((k) => k !== 'SERVICE_ACCOUNT')
                    .map((role, key) => (
                      <option key={key} value={role}>
                        {capitalizeFirstLetter(role)}
                      </option>
                    ))
                ) : (
                  <option value="PARTNER">Partner</option>
                )}
              </select>
            </Col>
          </FormGroup>
          <div className="float-end">
            <Button type="button" color="secondary" className="me-2" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </form>
      </div>
      {profileSetting ? (
        <div style={{ paddingTop: '3rem' }}>
          <FormGroup row>
            <Col md="3">{showVerification ? <label>Verification Code</label> : null}</Col>
            <Col md="3">{showVerification ? <input id="otp" /> : null}</Col>
            <Col md="6">
              <div>
                {showVerification ? (
                  <Button type="button" color="primary" className="me-2" onClick={verify2FA}>
                    Verify Code
                  </Button>
                ) : null}
                {!showVerification ? (
                  <Button type="button" color="primary" className="me-2" onClick={activate2FA}>
                    Activate 2FA
                  </Button>
                ) : null}
              </div>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3"></Col>
            <Col md="3">
              <canvas id="canvas"></canvas>
            </Col>
          </FormGroup>
        </div>
      ) : null}
      <XMAuditTable show={isEdit} audit={operator.one} />
    </div>
  );
};

export default OperatorForm;
